/**
 * Minimal fetch wrapper for the Predikt backend.
 *
 * Reads `VITE_API_BASE_URL` (typed as `string | undefined` per WR-003 — Vite
 * substitutes the literal `undefined` when unset) and validates at first use,
 * not at module load, so a missing env var fails loudly only when something
 * actually tries to call the API rather than crashing the whole bundle boot.
 *
 * Backend error envelope (per project CLAUDE.md):
 *   { "error": { "code": "...", "message": "...", "details": {...} } }
 *
 * On non-2xx, throws `ApiError` with `code` / `message` / `status` / `details`
 * pulled from the envelope when present, falling back to `UNKNOWN` + status.
 *
 * Forward-compat: when Zod is added to `web/` (CLAUDE.md recommends but it is
 * not yet installed), wrap the returned body with `schema.parse(body)` at the
 * caller site (`lib/api/waitlist.ts` etc.) — `request<T>` deliberately stays
 * schema-free so the parse step is explicit, not hidden.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

export type ApiErrorEnvelope = {
  error: { code: string; message: string; details?: unknown };
};

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;

  constructor(code: string, message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

function isErrorEnvelope(body: unknown): body is ApiErrorEnvelope {
  return (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    typeof (body as { error: unknown }).error === 'object' &&
    (body as { error: unknown }).error !== null
  );
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  if (!BASE_URL) {
    throw new ApiError(
      'CONFIG_MISSING',
      'VITE_API_BASE_URL is not set. The API client cannot run without it.',
      0,
    );
  }

  const headers: HeadersInit = {
    Accept: 'application/json',
    ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
    ...init?.headers,
  };

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, { ...init, headers });
  } catch (cause) {
    // Network failure, CORS, DNS, offline — fetch rejects rather than returning.
    throw new ApiError(
      'NETWORK_ERROR',
      cause instanceof Error ? cause.message : 'Network request failed',
      0,
    );
  }

  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // Non-JSON body — leave `body` as null and fall through to status check.
    }
  }

  if (!res.ok) {
    if (isErrorEnvelope(body)) {
      throw new ApiError(body.error.code, body.error.message, res.status, body.error.details);
    }
    throw new ApiError('UNKNOWN', `HTTP ${res.status}`, res.status);
  }

  return body as T;
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>(path),
  post: <T>(path: string, data: unknown): Promise<T> =>
    request<T>(path, { method: 'POST', body: JSON.stringify(data) }),
};
